import {
  JOINT_ACTIONS, ROLE_WEIGHT, MINIMUM_WEIGHTED_SETS,
  type Zone, type CoverageRole, type JointActionInfo,
} from "./coverageVocabulary";

/* The coverage matrix, as a pure function of a week.
 *
 * `docs/JOINT-ACTION-ENGINE.md` §6.1 — the owner's own document — step 3 of its §8: *"Build the
 * coverage matrix view for a single athlete-week. Ship it as a coach tool BEFORE wiring it into
 * generation."* Nothing here writes anything and nothing here is read by the generator.
 *
 * ── WHY THE RULES ARE WRITTEN TWICE AND THE VOCABULARY IS NOT ─────────────────────────────
 *
 * `coverageVocabulary.ts` is GENERATED from `JointActionCoverage.swift`: the 42 actions, the
 * zones, the role weights and the threshold have exactly one authorship, and a harness ruler
 * regenerates it every run. A list typed into two systems is this repo's defining bug class with
 * an anatomy in it.
 *
 * The state LADDER below is written in TypeScript as well as in Swift, and that is the honest
 * cost of the split rather than an oversight: the Swift harness cannot execute TypeScript, and a
 * rule with no runner is a comment with a type annotation. Both sides are executed —
 * `coverage.test.ts` here, `joint-action-1-the-coverage-vocabulary` there — over the same
 * fixtures, and the Swift gate asserts this file names every state Swift has.
 *
 * ⚠️ KEYED ON THE EXERCISE **ID**, NOT ITS NAME. The seed resolves names once, at write time,
 * because the two catalogues this repo carries hold different ids for the same movement. Inside
 * the app the id is exact, and matching on a name here would reintroduce the fuzziness the seed
 * paid to get rid of.
 */

export type CoverageState =
  /** At or above the minimum. ⚠️ The only state that is sound on partial data. */
  | "covered"
  /** Trained, but under the minimum. */
  | "thin"
  /** Nothing trains it, and the athlete declared that on purpose. */
  | "zeroDeclared"
  /** The athlete deferred this cell and the week loads it anyway. */
  | "declaredButTrained"
  /** Nothing trains it and nobody decided that. The finding. */
  | "zeroAccidental"
  /** The week holds exercises this model cannot yet describe. Never reported as a hole. */
  | "provisional";

export type CoverageRow = {
  readonly exerciseId: string;
  readonly action: string;
  readonly zone: Zone;
  readonly role: CoverageRole;
  readonly note: string;
};

export type WeekEntry = {
  readonly exerciseId: string;
  readonly name: string;
  readonly sets: number;
};

/** His §7's declared zero, one per CELL. The athlete declares an ACTION and `deferrals.ts`
  * expands it across the zones that action carries. */
export type Deferral = {
  readonly action: string;
  readonly zone: Zone;
  readonly reason: string;
  readonly reviewOn: string;
};

/**
 * ✅ THE DECLARED ZERO HAS A HOME NOW, AND THIS CONSTANT RECORDS WHAT IT USED TO SAY.
 *
 * It read: *"a declared zero needs an (athlete, cell) table; none exists, so none is shown"* —
 * true from step 2 until 2026-08-31, and the reason both screens printed an apology instead of a
 * deferral. His §7 asks the engine to distinguish an ACCIDENTAL zero from a DECLARED one, the
 * RULE below has honoured one since it was written, and the input was the missing half.
 *
 * What it needed was not the table this sentence asked for. Measured live before deciding: of 15
 * profiles exactly ONE carries any injury, and the athlete whose `(forearm.supination, MID)`
 * rendered `Covered · 3.0 weighted sets` has none — so hanging a deferral off an injury would
 * have made the single worked example unreachable. And a table of its own would have needed new
 * RLS policies, which are the owner's, to buy nothing this feature uses. It is a column on
 * `profiles`, inheriting the row policy that already lets a coach read `injuries_data`, written
 * by the athlete and readable by the coach with no policy added at all.
 *
 * ⚠️ THE CONSTANT ITSELF IS DELETED AND THE HISTORY IS THIS COMMENT, WHICH IS WHERE IT BELONGED.
 * It was briefly rewritten in the past tense and rendered on both screens, and that was wrong for
 * a reason worth keeping: a coach reading a coverage table needs to know HOW TO READ THE ZEROS in
 * front of them, not when a column landed. An exported string with no reader is a write-only
 * member, which this repo deletes rather than keeps for sentiment. `useCoverageWeek` reads the
 * declarations now and `deferrals.ts` writes what the screens say about them.
 */
export type MatrixInput = {
  readonly week: readonly WeekEntry[];
  readonly rows: readonly CoverageRow[];
  readonly deferrals: readonly Deferral[];
};

export type CellReading = {
  readonly action: JointActionInfo;
  readonly zone: Zone;
  readonly key: string;
  /** ⚠️ A LOWER BOUND whenever `undescribed` is non-empty. Coverage is MONOTONE. */
  readonly weightedSets: number;
  /** The `prime` share. A cell can clear the minimum on bracing alone; this is how you can tell. */
  readonly primeWeighted: number;
  readonly state: CoverageState;
  readonly deferral: Deferral | null;
};

export function cellKey(action: string, zone: Zone): string {
  return `${action}|${zone}`;
}

/** The exercises in the week that no coverage row describes. The matrix's own honesty column. */
export function undescribed(input: MatrixInput): WeekEntry[] {
  const described = new Set(input.rows.map((r) => r.exerciseId));
  const seen = new Set<string>();
  const out: WeekEntry[] = [];
  for (const e of input.week) {
    if (described.has(e.exerciseId)) continue;
    if (seen.has(e.exerciseId)) continue;
    seen.add(e.exerciseId);
    out.push(e);
  }
  return out;
}

/**
 * One cell's reading.
 *
 * 🔴 THE MONOTONICITY ARGUMENT IS THE WHOLE DESIGN, and it is what makes a matrix over a partly
 * described week honest instead of misleading. An undescribed exercise and an exercise that
 * genuinely covers nothing are indistinguishable, and the naive version fails in the FLATTERING
 * direction, because a screen full of confident ZEROs looks exactly like a working audit. His own
 * week is now described end to end, so its zeros are findings — **the rule still has to hold**,
 * because the next athlete's week will contain exercises nobody has described.
 *
 * Describing another exercise can only ADD weight to a cell. So a cell already at or above the
 * minimum is at or above it whatever the rest of the week turns out to be, and `covered` is
 * reported as fact; every other state could still move, so while any exercise is undescribed they
 * collapse to `provisional`.
 *
 * ⚠️ THE DEFERRAL IS TESTED FIRST, AND THAT ORDER IS LOAD-BEARING. Testing it after the threshold
 * lets a measured cell report `covered` and DELETES the athlete's own statement from the output.
 */
export function reading(
  action: JointActionInfo,
  zone: Zone,
  input: MatrixInput,
  complete: boolean,
): CellReading {
  const deferral =
    input.deferrals.find((d) => d.action === action.id && d.zone === zone) ?? null;

  let weighted = 0;
  let prime = 0;
  const setsById = new Map<string, number>();
  for (const e of input.week) setsById.set(e.exerciseId, (setsById.get(e.exerciseId) ?? 0) + e.sets);
  for (const r of input.rows) {
    if (r.action !== action.id || r.zone !== zone) continue;
    const sets = setsById.get(r.exerciseId);
    if (sets === undefined) continue;
    const w = ROLE_WEIGHT[r.role] * sets;
    weighted += w;
    if (r.role === "prime") prime += w;
  }

  let state: CoverageState;
  if (deferral !== null) {
    state = weighted > 0 ? "declaredButTrained" : "zeroDeclared";
  } else if (weighted >= MINIMUM_WEIGHTED_SETS) {
    state = "covered";
  } else if (!complete) {
    state = "provisional";
  } else if (weighted > 0) {
    state = "thin";
  } else {
    state = "zeroAccidental";
  }

  return {
    action, zone, key: cellKey(action.id, zone),
    weightedSets: weighted, primeWeighted: prime, state, deferral,
  };
}

/** Every cell, in taxonomy order. The caller decides what to SHOW. */
export function matrix(input: MatrixInput): CellReading[] {
  const complete = undescribed(input).length === 0;
  const out: CellReading[] = [];
  for (const a of JOINT_ACTIONS) {
    for (const z of a.zones) out.push(reading(a, z, input, complete));
  }
  return out;
}

/**
 * The audit set: cells at least one described exercise in the LIBRARY can fill.
 *
 * ⚠️ IT IS THE ANSWER TO THE WALL-OF-WARNINGS PROBLEM AND IT REUSES A MECHANISM THIS REPO ALREADY
 * MEASURED. `WeeklyVolumeLedger.poolServes` exists because the volume repair aborted in 128 of 128
 * live athlete-weeks chasing `Calves` — a floor the athlete's pool could not serve. The answer
 * there was to exclude what nothing reachable can fill and report it once, as a LIBRARY finding
 * with a different owner. A cell no described exercise covers is the same shape.
 */
export function servable(rows: readonly CoverageRow[]): Set<string> {
  return new Set(rows.map((r) => cellKey(r.action, r.zone)));
}

/**
 * §14.13's FOUR answers to *"can anything prime this cell"*, and they are four because the OWNERS
 * are four. `JointActionCoverage.PrimeVerdict`'s twin, and `joint-action-5`'s ruler derives this
 * union from `PrimeVerdict.allCases` so neither side can grow a case the other has never heard of.
 *
 * | verdict | who fixes it |
 * |---|---|
 * | `primed` | nobody |
 * | `primedOnlyOutOfReach` | the ATHLETE's equipment or stated limits — the catalogue is fine |
 * | `describedNeverPrimed` | the CATALOGUE: nothing published has this action as its defining job |
 * | `undescribed` | the coverage pass: no row mentions the cell at all |
 *
 * ⚠️ `undescribed` IS THE WEAKEST OF THE FOUR AND IS NOT A CONTENT GAP. It says only that nobody
 * authored a row. Measured on the published catalogue, 15 of the 20 undescribed cells are
 * `lengthened` — the zone §10's run record calls the least-evidenced field the coverage pass
 * wrote — so reading them as absences would be a claim about the catalogue built out of a
 * weakness in its description.
 */
export type PrimeVerdict =
  | "primed"
  | "primedOnlyOutOfReach"
  | "describedNeverPrimed"
  | "undescribed";

/**
 * 🔴 THE `reachable` ARGUMENT IS THE DESIGN, AND `servable` ABOVE IS WHY.
 *
 * `servable` borrowed `WeeklyVolumeLedger.poolServes`' MECHANISM and was handed the wrong
 * ARGUMENT — the library of what the athlete is already doing — so it could only ever answer
 * *"yes, they do what they do"*, and shipped reading zero by construction. One library here would
 * leave the identical trap open in the FLATTERING direction: a caller who means *this athlete* and
 * hands in the whole catalogue is told a cell is primed when nothing they can reach primes it.
 *
 * So the caller states the filter and `primedOnlyOutOfReach` cannot be computed without it —
 * `RepCounter.Sides`' shape, where the answer is not recoverable from the value so the caller says
 * which question it is asking. `goalGaps.ts` asks at LIBRARY scope and says so; a caller holding
 * an athlete's filtered pool gets the athlete answer from the same rule.
 *
 * ⚠️ KEYED ON THE EXERCISE **ID**, like every other rule in this file and unlike Swift's, which is
 * keyed on the NAME. The two catalogues this repo carries hold different ids for one movement, so
 * the seed resolved names once at write time; inside the app the id is exact.
 */
export function primeVerdict(
  action: string,
  zone: Zone,
  rows: readonly CoverageRow[],
  reachable: ReadonlySet<string>,
): PrimeVerdict {
  let described = false;
  let primedSomewhere = false;
  for (const r of rows) {
    if (r.action !== action || r.zone !== zone) continue;
    described = true;
    if (r.role !== "prime") continue;
    if (reachable.has(r.exerciseId)) return "primed";
    primedSomewhere = true;
  }
  if (primedSomewhere) return "primedOnlyOutOfReach";
  return described ? "describedNeverPrimed" : "undescribed";
}

/** Every exercise the library describes — the `reachable` set for a LIBRARY-scope question. */
export function describedExercises(rows: readonly CoverageRow[]): Set<string> {
  return new Set(rows.map((r) => r.exerciseId));
}

/**
 * 🔴 CAN THE AUDIT SET REPORT A HOLE AT ALL? MEASURED ON HIS OWN WEEK, TODAY, THE ANSWER IS NO —
 * AND IT IS NO **BY CONSTRUCTION**.
 *
 * The only thing `servable` can ever report is a cell that is servable AND empty. The described
 * library is currently exactly the 74 exercises of the athlete's own week, so every cell it
 * mentions is a cell the week trains: **servable ∩ zero is the empty set, whatever the athlete
 * does.** Live after the backfill: 72 servable cells, 46 accidental zeros, 0 of them servable.
 *
 * ⚠️ IT WAS INVISIBLE WHILE THE BACKFILL WAS PARTIAL. With 30 of 74 described, every uncovered
 * cell was `provisional` rather than `zeroAccidental`, so there were no zeros for the audit set to
 * fail to find and "0 servable zeros" read exactly like a clean audit.
 *
 * **A caller must ask this before filtering on `servable`.** Hiding cells on the authority of an
 * audit set that cannot find a hole hides every finding the screen has — which is precisely what
 * this screen did until the backfill made the zeros real.
 */
export function auditSetCanFindAHole(input: MatrixInput): boolean {
  const trained = new Set(input.week.map((e) => e.exerciseId));
  return input.rows.some((r) => !trained.has(r.exerciseId));
}

export type MatrixSummary = {
  readonly covered: number;
  readonly thin: number;
  readonly zeroAccidental: number;
  readonly zeroDeclared: number;
  readonly declaredButTrained: number;
  readonly provisional: number;
  readonly noPrimeMover: number;
  readonly describedExercises: number;
  readonly weekExercises: number;
  readonly describedSets: number;
  readonly weekSets: number;
};

/**
 * What the screen puts above the grid.
 *
 * ⚠️ IT REPORTS BOTH DENOMINATORS — EXERCISES AND SETS — AND NEITHER MAY STAND IN FOR THE OTHER.
 * The first 30 backfilled were 40.5% of the week's exercises and 55% of its sets, and the two
 * numbers answer different questions: how much of the TAXONOMY is described, and how much of the
 * NUMBERS are. CLAUDE.md records a denominator that was arithmetically true and described the
 * wrong population; printing one of these alone is that trap. Both read 100% for this athlete
 * now, and they will not for the next one.
 */
export function summarise(readings: readonly CellReading[], input: MatrixInput): MatrixSummary {
  const described = new Set(input.rows.map((r) => r.exerciseId));
  const seen = new Set<string>();
  let weekSets = 0;
  let describedSets = 0;
  let weekExercises = 0;
  let describedExercises = 0;
  for (const e of input.week) {
    weekSets += e.sets;
    if (described.has(e.exerciseId)) describedSets += e.sets;
    if (!seen.has(e.exerciseId)) {
      seen.add(e.exerciseId);
      weekExercises += 1;
      if (described.has(e.exerciseId)) describedExercises += 1;
    }
  }
  const count = (s: CoverageState) => readings.filter((r) => r.state === s).length;
  return {
    covered: count("covered"),
    thin: count("thin"),
    zeroAccidental: count("zeroAccidental"),
    zeroDeclared: count("zeroDeclared"),
    declaredButTrained: count("declaredButTrained"),
    provisional: count("provisional"),
    noPrimeMover: readings.filter((r) => r.weightedSets > 0 && r.primeWeighted === 0).length,
    describedExercises, weekExercises, describedSets, weekSets,
  };
}

/** Cells grouped by joint, in taxonomy order — the grid's row order. */
export function byJoint(readings: readonly CellReading[]): { joint: string; label: string; readings: CellReading[] }[] {
  const out: { joint: string; label: string; readings: CellReading[] }[] = [];
  for (const r of readings) {
    const last = out[out.length - 1];
    if (last !== undefined && last.joint === r.action.joint) last.readings.push(r);
    else out.push({ joint: r.action.joint, label: r.action.jointLabel, readings: [r] });
  }
  return out;
}

/** What a state says, in one phrase. The screen never spells a state's name at a reader. */
export function stateLabel(state: CoverageState): string {
  switch (state) {
    case "covered": return "Covered";
    case "thin": return "Thin";
    case "zeroDeclared": return "Deferred";
    case "declaredButTrained": return "Deferred, but trained";
    case "zeroAccidental": return "Nothing trains this";
    case "provisional": return "Not yet known";
  }
}
